import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/useAuth";
import { globalKey } from "../query/queryKeys";
import {refreshQueriesAfterWrite} from '../../../../../../shared/frontend/operations/refreshQueriesAfterWrite';

const CHANNEL_AVAILABILITY_STALE_MS = 5 * 60 * 1000;

/** Kanäle, die nur mit konfigurierter Integration existieren — plus E-Mail als Grundausstattung. */
export type MessagingChannel = "email" | "letter" | "sms" | "whatsApp";

export type ChannelAvailabilityDto = { canSend: boolean; hasHistory: boolean };

export type ChannelAvailabilitySummaryDto = Record<MessagingChannel, ChannelAvailabilityDto>;

export type ChannelFlags = Record<MessagingChannel, boolean>;

export type ChannelAvailability = {
  /** Erste Antwort steht noch aus. Bis dahin gilt: nur E-Mail. */
  isPending: boolean;
  /** Über den Kanal kann heute versendet werden (aktive Integration). */
  canSend: ChannelFlags;
  /** Es gibt bereits Nachrichten dieses Kanals — hält Listen-Reiter nach dem Entfernen einer Integration offen. */
  hasHistory: ChannelFlags;
  /** Reiter/Listen: versendbar oder Bestand vorhanden. */
  isVisible: ChannelFlags;
};

const CHANNEL_AVAILABILITY_KEY = globalKey("communication", "channel-availability");

/**
 * Bis zur ersten Antwort gibt es keinen Kanal außer E-Mail. Ein Kanal darf nie kurz aufblitzen
 * und dann verschwinden — die Oberfläche wächst, sie schrumpft nicht.
 */
const EMAIL_ONLY: ChannelFlags = { email: true, letter: false, sms: false, whatsApp: false };

function flags(summary: ChannelAvailabilitySummaryDto | undefined, pick: (entry: ChannelAvailabilityDto) => boolean): ChannelFlags {
  if (!summary) return EMAIL_ONLY;
  return {
    email: true,
    letter: pick(summary.letter),
    sms: pick(summary.sms),
    whatsApp: pick(summary.whatsApp)
  };
}

/**
 * **Einzige** Quelle für die Frage „gibt es den Kanal für diesen Mandanten?“. Brief, SMS und
 * WhatsApp existieren nur mit aktiver Integration (Brief: onlinebrief24.de). Wer die Frage
 * stellt, fragt hier — nicht `by-capability`, das die Integrations-Liste für Versandkacheln liefert
 * und Rechte verlangt, die nicht jeder Nutzer hat.
 */
export function useChannelAvailability(): ChannelAvailability {
  const auth = useAuth();
  const query = useQuery({
    queryKey: CHANNEL_AVAILABILITY_KEY,
    queryFn: async () => await auth.api.get<ChannelAvailabilitySummaryDto>("/api/v1/integration-connections/channel-availability"),
    staleTime: CHANNEL_AVAILABILITY_STALE_MS
  });
  return React.useMemo(() => {
    const canSend = flags(query.data, (entry) => entry.canSend);
    const hasHistory = flags(query.data, (entry) => entry.hasHistory);
    return {
      isPending: query.isPending,
      canSend,
      hasHistory,
      isVisible: {
        email: true,
        letter: canSend.letter || hasHistory.letter,
        sms: canSend.sms || hasHistory.sms,
        whatsApp: canSend.whatsApp || hasHistory.whatsApp
      }
    };
  }, [query.data, query.isPending]);
}

/** Nach dem Anlegen, Ändern oder Löschen einer Integration: Kanäle neu lesen. */
export function useInvalidateChannelAvailability(strict=false): () => Promise<void> {
  const queryClient = useQueryClient();
  return React.useCallback(
    () => strict?refreshQueriesAfterWrite(queryClient,{queryKey:CHANNEL_AVAILABILITY_KEY}):queryClient.invalidateQueries({ queryKey: CHANNEL_AVAILABILITY_KEY }),
    [queryClient,strict]
  );
}

/**
 * Kanal-Kennungen einer Auswahl (z. B. `allowedChannels` des Composers: `"email"`, `"letter"`,
 * `"sms"`, `"whatsapp"`) auf die vorhandenen Kanäle einschränken. SMS und WhatsApp fallen ohne
 * Integration weg. `"letter"` bleibt: Ein Brief kann immer lokal gedruckt werden — ob er zusätzlich
 * über eine Integration rausgeht, entscheidet die Versandkachel (`DeliveryChannelSection`).
 * Übrige Kennungen bleiben stehen — sie sind keine Integrationskanäle (etwa `"debt_collection"`).
 */
export function filterChannelsByAvailability<T extends string>(channels: readonly T[], canSend: ChannelFlags): T[] {
  return channels.filter((channel) => {
    const key = channel.toLowerCase();
    if (key.includes("whatsapp")) return canSend.whatsApp;
    if (key.includes("sms")) return canSend.sms;
    return true;
  });
}
